import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { PARTNER_KEYS } from '../lib';
import { PartnerApi } from '../api';
import type {
  PartnerById,
  PartnerCreatePayload,
  PartnerListItem,
  PartnerUpdatePayload,
  PartnerItem,
  PartnerItemListParams,
  PartnerValidateInnPayload,
  PartnerProductItem,
  PartnerProductPayload,
  PartnerProductIndexItem,
  PartnerProductIndexParams,
  PartnerProductPricePayload,
} from './types';

export const usePartnerItemList = (params?: PartnerItemListParams) => {
  const hasNameFilter = Boolean(params?.name && params.name.trim().length >= 1);
  const hasIdsFilter = Boolean(params?.ids && params.ids.length > 0);

  return useQuery<ApiResponse<PartnerItem[]>, Error, PartnerItem[] | undefined>({
    queryKey: PARTNER_KEYS.itemList(params),
    queryFn: () => PartnerApi.getItemList(params),
    enabled: hasNameFilter || hasIdsFilter,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const usePartnerList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<PartnerListItem>>({
    queryKey: PARTNER_KEYS.list(params),
    queryFn: () => PartnerApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const usePartnerById = (id: number) => {
  return useQuery<ApiResponse<PartnerById>>({
    queryKey: PARTNER_KEYS.detail(id),
    queryFn: () => PartnerApi.getById(id),
    enabled: !!id,
  });
};

export const useValidatePartnerInn = () => {
  return useMutation({
    mutationFn: (payload: PartnerValidateInnPayload) => PartnerApi.validateInn(payload),
  });
};

export const useCreatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerCreatePayload) => PartnerApi.create(payload),
    onSuccess: (data) => {
      if (!data.success) return;
      queryClient.invalidateQueries({ queryKey: PARTNER_KEYS.all });
    },
  });
};

export const useUpdatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerUpdatePayload) => PartnerApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_KEYS.all });
    },
  });
};

export const useDeletePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => PartnerApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_KEYS.all });
    },
  });
};

export const usePartnerProductList = (partnerId: number) => {
  return useQuery<ApiResponse<PartnerProductItem[]>>({
    queryKey: PARTNER_KEYS.products(partnerId),
    queryFn: () => PartnerApi.getProductList(partnerId),
    enabled: !!partnerId,
    refetchOnWindowFocus: false,
  });
};

export const useCreatePartnerProducts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerProductPayload) => PartnerApi.createProducts(payload),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({ queryKey: PARTNER_KEYS.products(payload.partner_id) });
    },
  });
};

export const useUpdatePartnerProducts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerProductPayload) => PartnerApi.updateProducts(payload),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({ queryKey: PARTNER_KEYS.products(payload.partner_id) });
    },
  });
};

export const usePartnerProductIndex = (params?: PartnerProductIndexParams) => {
  return useQuery<ApiResponseList<PartnerProductIndexItem>>({
    queryKey: PARTNER_KEYS.productIndex(params),
    queryFn: () => PartnerApi.getProductIndex(params),
    placeholderData: keepPreviousData,
  });
};

export const useUpdatePartnerProductPrice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerProductPricePayload) => PartnerApi.updateProductPrice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTNER_KEYS.productIndexAll });
    },
  });
};
